import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, MessageCircle, Phone, Mail, HelpCircle } from "lucide-react";

// In-portal "Help & Support → Contact Us" page. Rendered inside PortalShell
// (sidebar + topbar + light theme), so it only renders its own content.
// NOTE: action controls are <button>s (not <a>/<Link>) on purpose — lightMode.css
// forces every anchor to black text with a gold :hover, which would break these
// buttons; buttons are unaffected by those global anchor rules.
export default function ContactSupport() {
  const navigate = useNavigate();
  const SUPPORT_PHONE = "+91 8826178416";
  const SUPPORT_EMAIL = "support@jurident.com";
  const OTHER_EMAIL = "connect@valscotech.com";
  const WHATSAPP_URL = "https://wa.me/918826178416";

  const cards = [
    {
      key: "chat",
      icon: MessageCircle,
      tint: "bg-indigo-50 text-indigo-500",
      title: "Support Chat",
      lines: ["Chat with our support team", "24/7 online support"],
      button: { label: "Start Chat", solid: true, onClick: () => window.open(WHATSAPP_URL, "_blank") },
    },
    {
      key: "call",
      icon: Phone,
      tint: "bg-orange-50 text-orange-500",
      title: "Call Center",
      lines: ["Speak with our support team", SUPPORT_PHONE],
      button: { label: "Call Now", onClick: () => window.open(`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`) },
    },
    {
      key: "email",
      icon: Mail,
      tint: "bg-blue-50 text-blue-500",
      title: "Email Support",
      lines: ["Send us an email and we will respond shortly"],
      emailLink: SUPPORT_EMAIL,
      button: { label: "Send Email", onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`) },
    },
    {
      key: "faq",
      icon: HelpCircle,
      tint: "bg-emerald-50 text-emerald-500",
      title: "FAQ",
      lines: ["Find answers to common questions and issues"],
      button: { label: "View FAQ", onClick: () => navigate("/portal/faq") },
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="text-slate-500">Help &amp; Support</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Contact Us</span>
        </div>

        {/* Header */}
        <div className="mt-2">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Contact Us</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            We are here to help! Choose the best way to get in touch with us.
          </p>
        </div>

        {/* Support option cards */}
        <div className="mt-32 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map(({ key, icon: Icon, tint, title, lines, emailLink, button }) => (
            <div key={key} className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tint}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h3 className="mt-3 text-sm font-bold text-slate-900">{title}</h3>
                {lines.map((line, i) => (
                  <p key={i} className={`${i === 0 ? "mt-1" : ""} text-xs text-slate-500`}>{line}</p>
                ))}
                {emailLink && (
                  <button
                    type="button"
                    onClick={() => window.open(`mailto:${emailLink}`)}
                    className="mt-1 inline-block text-xs font-medium text-blue-600 hover:underline"
                  >
                    {emailLink}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={button.onClick}
                className={
                  button.solid
                    ? "light-button-text-white mt-4 w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
                    : "mt-4 w-full rounded-lg border border-indigo-200 bg-white px-4 py-2 text-center text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50"
                }
              >
                {button.label}
              </button>
            </div>
          ))}
        </div>

        {/* Other Enquiries */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <h3 className="text-sm font-bold text-slate-900">Other Enquiries</h3>
          <p className="mt-1 text-xs text-slate-500">
            For partnership, media or any other non-support related enquiries please email us at{" "}
            <button
              type="button"
              onClick={() => window.open(`mailto:${OTHER_EMAIL}`)}
              className="font-medium text-indigo-600 hover:underline"
            >
              {OTHER_EMAIL}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
